import type { LucideIcon } from 'lucide-react';
import {
  ClipboardCheck, FileBadge2, FileSignature, GraduationCap, Landmark,
  Images, Award, BellRing, BriefcaseBusiness, ClipboardCheck as ReviewIcon, CreditCard, PackageCheck, CalendarClock, ClipboardList, FileText, Gauge, HeartHandshake, Inbox, LayoutDashboard, Lightbulb, Mail, Radio, ShieldCheck, SlidersHorizontal, Target, Tv, UserPlus, UserRound, Users, Home,
} from 'lucide-react';
import {
  Activity, Banknote, Building2, CalendarDays, CalendarHeart, CheckSquare, Flag, Link2, MailQuestion, MessageSquareWarning, MessagesSquare, NotebookPen, Paperclip, Plus, StickyNote, UsersRound, Wallet,
} from 'lucide-react';
import type { Permission } from '@/auth/permissions';
import type { MessageId } from '@/i18n/messages';
import type { StudentAccountKind, EffectiveRole } from '@/lib/types';
import { RECORDS_NAV_INSTITUTION, RECORDS_NAV_ME, RECORDS_NAV_PLATFORM, RECORDS_NAV_SCHEDULE } from '@/pages/records/nav';

export interface NavItem {
  to: string;
  /** معرِّف الرسالة لا النصّ — الاسم يُترجم لحظة العرض في AppShell */
  labelId: MessageId;
  icon: LucideIcon;
  /** يظهر فقط إن ملك المستخدم هذه الصلاحية */
  permission?: Permission;
  /** أو إن كان أحد أدواره ضمن هذه القائمة (للشاشات التي لا تُحكم بصلاحية واحدة) */
  anyRole?: EffectiveRole[];
  /** يُخفى عمّن يحمل أحد هذه الأدوار — لشاشة يملك صلاحيتها لكنها ليست مكانه */
  notRole?: EffectiveRole[];
  /** لا يظهر إلا لطالبٍ حسابه من هذا النوع (بوابة التمارين لحساب التعلّم مثلاً) */
  studentKind?: StudentAccountKind;
  /** يُخفى عن طالبٍ حسابه من هذه الأنواع (حسابا التعلّم والطالب العادي لا يريان التقارير ولا التوصيات) */
  notStudentKind?: readonly StudentAccountKind[];
  /** اسمٌ بديل حين يكون المستخدم طالباً (تقاريري لا تقارير أبنائي) */
  studentLabelId?: MessageId;
}
export interface NavGroup { labelId: MessageId; items: NavItem[] }

export const NAV: NavGroup[] = [
  {
    labelId: 'nav.group.oversight',
    items: [
      { to: '/oversight', labelId: 'nav.oversight.linked', icon: Landmark, permission: 'oversight.view' },
    ],
  },
  ...RECORDS_NAV_PLATFORM,
  {
    labelId: 'nav.group.institution',
    items: [
      // المعلم يملك institution.view ليكون له سياق مؤسسة، لكن مكانه «طلبتي» لا الرئيسية
      { to: '/app', labelId: 'nav.app.dashboard', icon: LayoutDashboard, permission: 'institution.view', notRole: ['TEACHER'] },
      { to: '/app/review', labelId: 'nav.app.review', icon: ReviewIcon, permission: 'review.inbox' },
      // قائمةُ الطلبة للإدارة وحدها (قرار 20 سبتمبر 2026): الأخصائي والمرشد يريان
      // «الطلبة المسندين إليهم»، والمعلّم «طلبتي» — لا القائمةَ الكاملة.
      { to: '/app/students', labelId: 'nav.app.students', icon: GraduationCap, permission: 'student.view', anyRole: ['INSTITUTION_ADMIN'] },
      { to: '/app/link-requests', labelId: 'nav.app.linkRequests', icon: UserPlus, permission: 'link_request.view' },
      { to: '/app/plans', labelId: 'nav.app.plans', icon: Target, permission: 'plan.view' },
      { to: '/app/preparations', labelId: 'nav.app.preparations', icon: ClipboardList, permission: 'preparation.view' },
      { to: '/app/sessions', labelId: 'nav.app.sessions', icon: CalendarClock, permission: 'session.view' },
      ...RECORDS_NAV_SCHEDULE,
      { to: '/app/reports', labelId: 'nav.app.reports', icon: FileText, permission: 'report.view' },
      // المشرفُ الداخلي لا قائمةَ حالاتٍ له — يدقّق الخططَ والأهداف من مركز المراجعة
      // (قرار صاحبة المنتج 20 سبتمبر 2026). والإدارةُ تُسنِد، والمختصُّ يرى المسندَ إليه.
      { to: '/app/caseload', labelId: 'nav.app.caseloadAdmin', icon: ClipboardCheck, permission: 'caseload.assign', anyRole: ['INSTITUTION_ADMIN'] },
      { to: '/app/caseload', labelId: 'nav.app.caseload', icon: ClipboardCheck, permission: 'caseload.view', anyRole: ['SPECIALIST', 'COUNSELOR'] },
      { to: '/app/counseling', labelId: 'nav.app.counseling', icon: HeartHandshake, permission: 'counseling.plan.manage' },
      { to: '/app/referrals', labelId: 'nav.app.referrals', icon: Inbox, permission: 'referral.view' },
      { to: '/app/users', labelId: 'nav.app.users', icon: Users, permission: 'user.view' },
      { to: '/app/invitations', labelId: 'nav.app.invitations', icon: Mail, permission: 'invitation.view' },
      { to: '/app/credentials', labelId: 'nav.app.credentials', icon: FileBadge2, permission: 'staff_credentials.view' },
      { to: '/app/settings', labelId: 'nav.app.settings', icon: ClipboardCheck, permission: 'institution.settings.update' },
      { to: '/app/branding', labelId: 'nav.app.branding', icon: Images, permission: 'institution.branding.manage' },
      { to: '/app/alerts', labelId: 'nav.app.alerts', icon: BellRing, permission: 'alert.view' },
      { to: '/app/indicators', labelId: 'nav.app.indicators', icon: Gauge, permission: 'indicator.view' },
      { to: '/app/rules', labelId: 'nav.app.rules', icon: SlidersHorizontal, permission: 'rule.view', notRole: ['PLATFORM_ADMIN'] },
      { to: '/app/excellence', labelId: 'nav.app.excellence', icon: Award, permission: 'excellence.scores.view' },
      { to: '/app/broadcast', labelId: 'nav.app.broadcast', icon: Tv, permission: 'broadcast.view' },
      { to: '/app/regulatory-scope', labelId: 'nav.app.regulatoryScope', icon: Landmark, permission: 'regulatory_scope.manage' },
      { to: '/app/integration', labelId: 'nav.app.integration', icon: Radio, permission: 'integration.view' },
      { to: '/app/billing', labelId: 'nav.app.billing', icon: CreditCard, permission: 'billing.view' },
      { to: '/app/jobs', labelId: 'nav.app.jobs', icon: BriefcaseBusiness, permission: 'job.manage' },
      { to: '/app/continuity', labelId: 'nav.app.continuity', icon: PackageCheck, permission: 'continuity.issue' },
      { to: '/app/audit', labelId: 'nav.app.audit', icon: ShieldCheck, permission: 'audit.view' },
      ...RECORDS_NAV_INSTITUTION,
    ],
  },
  {
    labelId: 'nav.group.me',
    items: [
      { to: '/me/home', labelId: 'nav.me.home', icon: Home, notRole: ['STUDENT', 'PLATFORM_ADMIN'] },
      // بنود الطالب (صفحتي، تماريني…) في قشرة الطفل `KidShell` لا هنا
      { to: '/me/class', labelId: 'nav.me.class', icon: GraduationCap, permission: 'teacher.roster.view' },
      { to: '/me/assignments', labelId: 'nav.me.assignments', icon: ClipboardList, permission: 'student.assignment.manage' },
      { to: '/me/roster-plans', labelId: 'nav.me.rosterPlans', icon: Target, permission: 'teacher.shared_plan.view' },
      // 30 سبتمبر 2026 — إحالةُ معلّم الصف عادت (`teacher.referral.create`): الإرشادُ يُقبل تلقائياً والتربيةُ الخاصة بأدلة
      { to: '/me/referrals', labelId: 'nav.me.referrals', icon: Inbox, permission: 'teacher.referral.create' },
      { to: '/me/children', labelId: 'nav.me.children', icon: Users, permission: 'student.view_own_children' },
      { to: '/me/reports', labelId: 'nav.me.reports', icon: FileText, permission: 'report.view_published', notStudentKind: ['LEARNING', 'GENERAL'], studentLabelId: 'nav.me.reports.student' },
      { to: '/me/recommendations', labelId: 'nav.me.recommendations', icon: Lightbulb, permission: 'counseling.recommendation.view_own', notStudentKind: ['LEARNING', 'GENERAL'] },
      { to: '/me/consents', labelId: 'nav.me.consents', icon: FileSignature, permission: 'consent.view_own' },
      { to: '/me/excellence', labelId: 'nav.me.excellence', icon: Award, permission: 'excellence.view_own' },
      { to: '/me/jobs', labelId: 'nav.me.jobs', icon: BriefcaseBusiness, permission: 'job.apply' },
      { to: '/me/continuity', labelId: 'nav.me.continuity', icon: PackageCheck, permission: 'student.view_own_children' },
      { to: '/me/professional', labelId: 'nav.me.professional', icon: UserRound, permission: 'professional.self.manage' },
      { to: '/me/invitations', labelId: 'nav.me.invitations', icon: Mail, permission: 'professional.self.manage' },
      ...RECORDS_NAV_ME,
    ],
  },
];

/* ─────────────── الإجراءاتُ السريعة (جولة الجوال، 26 سبتمبر 2026) ───────────────
 * إعدادٌ واحد لكل دور يغذّي `QuickActions` في أعلى الرئيسية واختصارَ الأيقونة «إجراء سريع»
 * (`/go/quick`). كلُّ بندٍ يُصفّى بصلاحيته (`roleHasPermission`) فلا يظهر إجراءٌ لا يملكه صاحبه،
 * ولا يتكرّر على الجوال ما في الشريط السفلي. والترتيبُ هنا هو الأولوية.
 * (مراجَعٌ على `permissions.ts` لا على الاقتراح الأولي — «رسائل الطلبة» و«رفع مرفق» بلا شاشةٍ
 * مستقلّة: A-111 وA-112.)
 */
export interface QuickAction {
  to: string;
  labelAr: string;
  icon: LucideIcon;
  permission?: Permission;
}

export const QUICK_ACTIONS: Partial<Record<EffectiveRole, QuickAction[]>> = {
  INSTITUTION_ADMIN: [
    { to: '/app/link-requests', labelAr: 'طلبات الربط', icon: Link2, permission: 'link_request.view' },
    { to: '/app/attendance', labelAr: 'الحضور اليوم', icon: CheckSquare, permission: 'attendance.view' },
    { to: '/app/review', labelAr: 'مركز المراجعة', icon: ReviewIcon, permission: 'review.inbox' },
  ],
  INTERNAL_SUPERVISOR: [
    { to: '/app/review', labelAr: 'مركز المراجعة', icon: ReviewIcon, permission: 'review.inbox' },
    { to: '/app/link-requests', labelAr: 'طلبات الربط', icon: Link2, permission: 'link_request.view' },
    { to: '/app/attendance', labelAr: 'الحضور اليوم', icon: CheckSquare, permission: 'attendance.view' },
  ],
  SPECIALIST: [
    { to: '/app/schedule', labelAr: 'جدولي اليوم', icon: CalendarDays, permission: 'schedule.view_own' },
    { to: '/app/preparations', labelAr: 'التحضير اليومي', icon: NotebookPen, permission: 'preparation.view' },
    { to: '/app/plans', labelAr: 'خطط الطلبة', icon: Target, permission: 'plan.view' },
    { to: '/app/attachments', labelAr: 'رفع مرفق', icon: Paperclip, permission: 'attachment.manage' },
  ],
  COUNSELOR: [
    { to: '/app/schedule', labelAr: 'جدولي اليوم', icon: CalendarDays, permission: 'schedule.view_own' },
    { to: '/app/counseling', labelAr: 'خطط الإرشاد', icon: HeartHandshake, permission: 'counseling.plan.manage' },
    { to: '/app/preparations', labelAr: 'التحضير اليومي', icon: NotebookPen, permission: 'preparation.view' },
    { to: '/app/attachments', labelAr: 'رفع مرفق', icon: Paperclip, permission: 'attachment.manage' },
  ],
  TEACHER: [
    { to: '/app/attendance', labelAr: 'سجّل الحضور', icon: CheckSquare, permission: 'attendance.record' },
    { to: '/me/assignments', labelAr: 'واجب جديد', icon: ClipboardList, permission: 'student.assignment.manage' },
    { to: '/me/roster-plans', labelAr: 'خطط طلبتي', icon: Target, permission: 'teacher.shared_plan.view' },
    { to: '/me/messages', labelAr: 'رسائل الطلبة', icon: MessagesSquare, permission: 'student.message.reply' },
  ],
  PARENT: [
    { to: '/me/children?add=1', labelAr: 'أضف طفلاً', icon: Plus, permission: 'guardian.self.manage' },
    { to: '/centers', labelAr: 'اربط طفلك بمؤسسة', icon: MailQuestion, permission: 'guardian.self.manage' },
    { to: '/consultants', labelAr: 'احجز استشارة', icon: MessagesSquare },
  ],
  CLIENT: [
    { to: '/consultants', labelAr: 'احجز استشارة', icon: CalendarHeart, permission: 'client.self' },
    { to: '/me/wallet', labelAr: 'محفظتي', icon: Wallet, permission: 'wallet.self.view' },
  ],
  PLATFORM_ADMIN: [
    { to: '/platform/health', labelAr: 'صحة التشغيل', icon: Activity, permission: 'platform.health.view' },
    { to: '/platform/contact-messages', labelAr: 'رسائل التواصل', icon: Inbox, permission: 'contact.manage' },
    { to: '/platform/content-reports', labelAr: 'بلاغات المحتوى', icon: Flag, permission: 'content.moderate' },
  ],
  // أقسامُ إدارة المنصة (0066) — كلٌّ بما يخصّه
  PLATFORM_SUPPORT: [
    { to: '/platform/accounts', labelAr: 'الحسابات', icon: UsersRound, permission: 'user.view_all' },
    { to: '/platform/contact-messages', labelAr: 'رسائل التواصل', icon: Inbox, permission: 'contact.manage' },
    { to: '/platform/complaints', labelAr: 'الشكاوى', icon: MessageSquareWarning, permission: 'complaint.review' },
    { to: '/platform/notes', labelAr: 'ملاحظات التحسين', icon: StickyNote, permission: 'platform_note.manage' },
  ],
  PLATFORM_ACADEMIES: [
    { to: '/platform/academies', labelAr: 'الأكاديميات', icon: GraduationCap, permission: 'academy.review' },
    { to: '/platform/course-providers', labelAr: 'مقدّمو الدورات', icon: FileBadge2, permission: 'course_provider.review' },
    { to: '/platform/content-reports', labelAr: 'بلاغات المحتوى', icon: Flag, permission: 'content.moderate' },
    { to: '/platform/notes', labelAr: 'ملاحظات التحسين', icon: StickyNote, permission: 'platform_note.manage' },
  ],
  PLATFORM_INSTITUTIONS: [
    { to: '/platform/institution-requests', labelAr: 'طلبات التسجيل', icon: Building2, permission: 'institution_request.review' },
    { to: '/platform/seat-requests', labelAr: 'طلبات المقاعد', icon: Users, permission: 'seat_request.decide' },
    { to: '/platform/institutions', labelAr: 'المؤسسات', icon: Landmark, permission: 'institution.view_all' },
    { to: '/platform/notes', labelAr: 'ملاحظات التحسين', icon: StickyNote, permission: 'platform_note.manage' },
  ],
  PLATFORM_FINANCE: [
    { to: '/platform/payouts', labelAr: 'طلبات السحب', icon: Banknote, permission: 'payout.decide' },
    { to: '/platform/pricing', labelAr: 'تسعير المنصة', icon: SlidersHorizontal, permission: 'platform.pricing.manage' },
  ],
};

/** أيُّ إعدادٍ يأخذ من يحمل أكثر من صفة — الأولُ الذي يحمله بهذا الترتيب */
export const QUICK_ROLE_ORDER: EffectiveRole[] = [
  'PLATFORM_ADMIN', 'PLATFORM_SUPPORT', 'PLATFORM_ACADEMIES', 'PLATFORM_INSTITUTIONS', 'PLATFORM_FINANCE', 'INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'TEACHER', 'PARENT', 'CLIENT',
];

